import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Grid, Typography, Button, Card, CardContent,
  Stack, Chip, Stepper, Step, StepLabel, StepContent,
  TextField, Alert, CircularProgress, Paper, Divider,
} from '@mui/material';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import CheckCircleIcon        from '@mui/icons-material/CheckCircleOutline';
import CheckCircleFilledIcon  from '@mui/icons-material/CheckCircle';
import SearchIcon             from '@mui/icons-material/Search';
import AssignmentIndIcon      from '@mui/icons-material/AssignmentInd';
import VerifiedUserIcon       from '@mui/icons-material/VerifiedUser';
import SchoolIcon             from '@mui/icons-material/School';
import GroupsIcon             from '@mui/icons-material/Groups';
import CardMembershipIcon     from '@mui/icons-material/CardMembership';
import RefreshIcon            from '@mui/icons-material/Refresh';
import ContratoAfiliacion     from '../components/ContratoAfiliacion';
import { translations, defaultLanguage } from '../data/Translations';
import ScrollIndicator         from '../components/ScrollIndicator';
import SideNav                 from '../components/SideNav';
import PagoInscripcionWidget  from '../components/PagoInscripcionWidget';
import {
  consultarEstadoAfiliacion,
  verificarDocumento,
  getEstadoPagoInscripcion,
} from '../services/ecolabApi';

// ── Colores Capellanías ──────────────────────────────────────────────────────
const C = {
  gold:     '#D4AF37',
  darkGold: '#B8960C',
  lightGold:'#F4E4BC',
  black:    '#1A1A1A',
  darkGrey: '#2D2D2D',
  cream:    '#FDF8E8',
  offWhite: '#F8F6F0',
};

// ── ID de Capellanías en el backend Ecolab ───────────────────────────────────
// Actualizar cuando se registre la entidad en el backend
const CAPELLANIAS_COOPERATIVA_ID = 6; // CooperativaID registrado en backend Ecolab

// ── CAPTCHA matemático ───────────────────────────────────────────────────────
function newCaptcha() {
  const ops = ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * 3)];
  let a, b, r;
  if (op === '+') { a = Math.floor(Math.random() * 15) + 1; b = Math.floor(Math.random() * 15) + 1; r = a + b; }
  else if (op === '-') { a = Math.floor(Math.random() * 15) + 5; b = Math.floor(Math.random() * a) + 1; r = a - b; }
  else { a = Math.floor(Math.random() * 9) + 2; b = Math.floor(Math.random() * 9) + 2; r = a * b; }
  return { a, b, op, r };
}

// ── Guía de pasos del proceso ────────────────────────────────────────────────
const guiaPasosIcons = [
  <AssignmentIndIcon sx={{ fontSize: 22 }} />,
  <VerifiedUserIcon  sx={{ fontSize: 22 }} />,
  <CardMembershipIcon sx={{ fontSize: 22 }}/>,
  <SchoolIcon        sx={{ fontSize: 22 }} />,
  <GroupsIcon        sx={{ fontSize: 22 }} />,
];
const guiaPasosColors = [C.darkGold, '#7c3aed', '#e65100', '#0288d1', '#1e8a3c'];

export default function Inscripcion() {
  const [lang, setLang] = useState(() => {
    const s = localStorage.getItem('preferredLanguage');
    return (s && translations[s]) ? s : defaultLanguage;
  });
  useEffect(() => {
    const handler = (e) => { if (e.key === 'preferredLanguage' && e.newValue && translations[e.newValue]) setLang(e.newValue); };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  const t = translations[lang]?.inscripcion || translations[defaultLanguage].inscripcion;

  const [contratoOpen,   setContratoOpen]   = useState(false);
  const [pagoConfirmado, setPagoConfirmado] = useState(false);
  const [solicitudInfo,  setSolicitudInfo]  = useState(null);

  // Pre-verificación
  const [preVerifDoc,         setPreVerifDoc]         = useState('');
  const [preVerifState,       setPreVerifState]       = useState('idle');
  const [preVerifOpen,        setPreVerifOpen]        = useState(false);
  const [preVerifCaptcha,     setPreVerifCaptcha]     = useState(() => newCaptcha());
  const [preVerifCaptchaResp, setPreVerifCaptchaResp] = useState('');
  const [preVerifCaptchaErr,  setPreVerifCaptchaErr]  = useState(false);

  // Consulta de estado
  const [docConsulta,        setDocConsulta]        = useState('');
  const [consultando,        setConsultando]        = useState(false);
  const [resultadoConsulta,  setResultadoConsulta]  = useState(null);
  const [errorConsulta,      setErrorConsulta]      = useState('');
  const [consultaCaptcha,    setConsultaCaptcha]    = useState(() => newCaptcha());
  const [consultaCaptchaResp,setConsultaCaptchaResp]= useState('');
  const [consultaCaptchaErr, setConsultaCaptchaErr] = useState(false);

  // ── Pre-verificación ─────────────────────────────────────────────────────
  const verificarElegibilidad = async () => {
    if (preVerifDoc.trim().length < 5) return;
    if (parseInt(preVerifCaptchaResp, 10) !== preVerifCaptcha.r) { setPreVerifCaptchaErr(true); return; }
    setPreVerifCaptchaErr(false);
    setPreVerifState('checking');
    try {
      const data = await verificarDocumento(preVerifDoc.trim(), CAPELLANIAS_COOPERATIVA_ID);
      setPreVerifCaptcha(newCaptcha()); setPreVerifCaptchaResp('');
      if (data.excluido) {
        setPreVerifState('excluido');
      } else if (!data.disponible) {
        const mismaEntidad = data.cooperativaId === CAPELLANIAS_COOPERATIVA_ID
          || data.cooperativaId === String(CAPELLANIAS_COOPERATIVA_ID);
        setPreVerifState(mismaEntidad ? 'taken' : 'takenOtraEntidad');
      } else {
        setPreVerifState('ok');
      }
    } catch {
      setPreVerifState('ok');
    }
  };

  const abrirFormulario = () => {
    setPreVerifOpen(false); setPreVerifState('idle'); setPreVerifDoc('');
    setPreVerifCaptcha(newCaptcha()); setPreVerifCaptchaResp(''); setPreVerifCaptchaErr(false);
    setContratoOpen(true);
  };

  // ── Consulta de estado ───────────────────────────────────────────────────
  const handleConsulta = async () => {
    if (!docConsulta.trim()) return;
    if (parseInt(consultaCaptchaResp, 10) !== consultaCaptcha.r) { setConsultaCaptchaErr(true); return; }
    setConsultaCaptchaErr(false); setConsultando(true); setResultadoConsulta(null); setErrorConsulta('');
    try {
      const res = await consultarEstadoAfiliacion(docConsulta.trim(), CAPELLANIAS_COOPERATIVA_ID);
      let src = res;
      if (res.multipleAfiliaciones && Array.isArray(res.afiliaciones)) {
        src = res.afiliaciones.find(a => a.cooperativaID === CAPELLANIAS_COOPERATIVA_ID) ?? res.afiliaciones[0] ?? res;
      }
      const mapped = {
        ...src,
        afiliadoId:                src.afiliadoID ?? src.afiliadoId ?? 0,
        estado:                    src.estado ?? src.Estado ?? '',
        nombres:                   src.nombres ?? src.Nombres ?? '',
        apellidos:                 src.apellidos ?? src.Apellidos ?? '',
        fechaSolicitud:            src.fechaAfiliacion ?? src.fechaCreacion ?? null,
        pagoInscripcionConfirmado: src.pagoInscripcionConfirmado ?? false,
        tieneSesionAgendada:       src.tieneSesionAgendada ?? false,
      };
      setSolicitudInfo(mapped);
      setResultadoConsulta(mapped);
      if (mapped.pagoInscripcionConfirmado) setPagoConfirmado(true);
    } catch {
      setErrorConsulta(t.consulta.errorNoEncontrado);
    } finally {
      setConsultando(false); setConsultaCaptcha(newCaptcha()); setConsultaCaptchaResp(''); setConsultaCaptchaErr(false);
    }
  };

  const estadoColor = {
    PENDIENTE:   '#ff6b00',
    APROBADO:    C.darkGold,
    RECHAZADO:   '#d32f2f',
    EN_REVISION: '#0288d1',
  };

  return (
    <Box sx={{ background: C.offWhite }}>

      <SideNav items={[
        { id: 'insc-proceso',   label: t.sideNav.proceso },
        { id: 'insc-requisitos',label: t.sideNav.requisitos },
        { id: 'insc-inscribir', label: t.sideNav.inscribirme },
        { id: 'insc-consulta',  label: t.sideNav.miEstado },
      ]} />

      {/* ═══ HERO ═══ */}
      <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: '100vh', display: 'flex', alignItems: 'flex-end' }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1531545514256-b1400bc00f31?w=1920&q=80&auto=format&fit=crop)',
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
        <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(26,26,26,0.88) 0%, rgba(26,26,26,0.55) 100%)`, zIndex: 1 }} />
        <ScrollIndicator />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2,
          pt: { xs: 14, md: 18 }, pb: { xs: 8, md: 10 }, px: { xs: 3, sm: 5, md: 8, lg: 10 } }}>
          <Box sx={{ mb: 2 }}>
            <Chip label={t.hero.chip} size="small"
              sx={{ background: `rgba(212,175,55,0.15)`, border: `1px solid rgba(212,175,55,0.4)`, color: C.gold, fontWeight: 700 }} />
          </Box>
          <Typography variant="h1" sx={{ color: '#fff', fontSize: { xs: '2.8rem', md: '5rem' }, mb: 2, fontWeight: 800 }}>
            {t.hero.title}{' '}
            <Box component="span" sx={{ color: C.gold, fontStyle: 'italic' }}>{t.hero.titleHighlight}</Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 580, lineHeight: 1.85, fontSize: '1.05rem', mb: 5 }}>
            {t.hero.subtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              onClick={() => setPreVerifOpen(true)}
              sx={{ py: 2, px: 5,
                background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`,
                color: C.gold,
                border: `1px solid ${C.gold}`,
                boxShadow: `0 8px 28px rgba(212,175,55,0.25)`,
                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 36px rgba(212,175,55,0.35)` } }}>
              Iniciar Solicitud
            </Button>
            <Button variant="outlined" size="large"
              onClick={() => document.getElementById('insc-consulta')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              sx={{ py: 2, px: 5, borderColor: 'rgba(255,255,255,0.6)', color: '#fff', borderWidth: 2,
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.10)', borderWidth: 2 } }}>
              Consultar Estado
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ═══ PROCESO ═══ */}
      <Box id="insc-proceso" sx={{ py: { xs: 8, md: 12 }, background: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label={t.proceso.chip} sx={{ background: `rgba(212,175,55,0.12)`, color: C.darkGold, fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3.5rem' }, color: C.black, fontWeight: 800 }}>
              El <Box component="span" sx={{ color: C.darkGold, fontStyle: 'italic' }}>Proceso</Box> de Inscripción
            </Typography>
          </Box>
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Stepper orientation="vertical" nonLinear>
                {t.proceso.pasos.map((p, i) => (
                  <Step key={i} active expanded>
                    <StepLabel StepIconComponent={() => (
                      <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: guiaPasosColors[i], color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 4px 16px ${guiaPasosColors[i]}40`, flexShrink: 0 }}>
                        {guiaPasosIcons[i]}
                      </Box>
                    )}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: C.black, ml: 1 }}>{p.title}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 1, mb: 1 }}>{p.desc}</Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 4, borderRadius: 4, border: `2px solid ${C.lightGold}`, background: C.cream }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: '14px',
                    background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GroupsIcon sx={{ color: C.gold, fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: C.black, fontSize: '1rem' }}>¿Por qué inscribirse?</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Beneficios para Capellanes</Typography>
                  </Box>
                </Box>
                <Stack spacing={1.5}>
                  {['✝️','🤝','📚','❤️','💰','🛡️','🌎'].map((e, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '1rem', flexShrink: 0 }}>{e}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.88rem' }}>{t.proceso.beneficios[i]}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Button variant="contained" fullWidth size="large" onClick={() => setPreVerifOpen(true)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 4, py: 1.8, borderRadius: 50,
                    background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`,
                    color: C.gold, border: `1px solid ${C.gold}`,
                    '&:hover': { background: C.darkGrey } }}>
                  Iniciar Solicitud de Inscripción
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ REQUISITOS ═══ */}
      <Box id="insc-requisitos" sx={{ py: { xs: 8, md: 12 }, background: C.offWhite }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label={t.requisitos.chip} sx={{ background: `rgba(212,175,55,0.12)`, color: C.darkGold, fontWeight: 700, mb: 2 }} />
              <Typography variant="h2" sx={{ mt: 1, mb: 4, fontSize: { xs: '2.2rem', md: '3.5rem' }, color: C.black, fontWeight: 800 }}>
                <Box component="span" sx={{ color: C.darkGold, fontStyle: 'italic' }}>Requisitos</Box>{' '}
                para inscribirse
              </Typography>
              <Stack spacing={2}>
                {t.requisitos.lista.map((r, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, borderRadius: 2,
                    background: `rgba(212,175,55,0.05)`, border: `1.5px solid rgba(212,175,55,0.15)`,
                    transition: 'all 0.25s', '&:hover': { border: `1.5px solid ${C.gold}`, background: `rgba(212,175,55,0.1)` } }}>
                    <CheckCircleFilledIcon sx={{ color: C.darkGold, fontSize: 20, mt: 0.1, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>{r}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, borderRadius: 4, border: `2px solid ${C.lightGold}`, background: C.cream, mb: 3 }}>
                <Typography sx={{ fontWeight: 800, color: C.darkGold, mb: 2, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Inversión inicial
                </Typography>
                <Stack spacing={2}>
                  {t.requisitos.items.map(({ label, valor, tipo }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      pb: 2, borderBottom: `1px solid rgba(212,175,55,0.2)`, '&:last-child': { border: 'none', pb: 0 } }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: C.black }}>{label}</Typography>
                        <Chip label={tipo} size="small" sx={{ mt: 0.3, height: 18, fontSize: '0.6rem', background: `rgba(212,175,55,0.12)`, color: C.darkGold, fontWeight: 700 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: C.darkGold }}>{valor}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Alert severity="info" sx={{ mt: 2.5, fontSize: '0.75rem', borderRadius: 2, background: 'rgba(2,136,209,0.08)' }}>
                  Pago seguro procesado por <strong>Wompi</strong> — pasarela certificada en Colombia
                </Alert>
              </Card>
              <Card sx={{ p: 3.5, borderRadius: 4, background: '#fff' }}>
                <Typography sx={{ fontWeight: 800, color: C.black, mb: 2, fontSize: '0.88rem' }}>{t.requisitos.modalidadesTitle}</Typography>
                <Stack spacing={1.5}>
                  {t.requisitos.modalidades.map(({ i, l, d }) => (
                    <Box key={l} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, borderRadius: 2,
                      background: `rgba(212,175,55,0.05)`, border: `1px solid rgba(212,175,55,0.12)` }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>{i}</Typography>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.black }}>{l}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{d}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ CTA CENTRAL ═══ */}
      <Box id="insc-inscribir" sx={{ py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden',
        background: `linear-gradient(135deg, ${C.black} 0%, ${C.darkGrey} 100%)` }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography variant="h2" sx={{ color: '#fff', mb: 3, fontSize: { xs: '2.5rem', md: '4rem' }, fontWeight: 800 }}>
            {t.cta.title}{' '}
            <Box component="span" sx={{ color: C.gold, fontStyle: 'italic' }}>{t.cta.titleHighlight}</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.80)', mb: 5, maxWidth: 520, mx: 'auto', lineHeight: 1.85 }}>
{t.cta.subtitle}
          </Typography>
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
            onClick={() => setPreVerifOpen(true)}
            sx={{ py: 2.2, px: 6, fontSize: '1rem', borderRadius: 50,
              background: `linear-gradient(135deg, ${C.darkGold}, ${C.gold})`,
              color: C.black, fontWeight: 800,
              boxShadow: `0 8px 30px rgba(212,175,55,0.4)`,
              '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 14px 40px rgba(212,175,55,0.5)` } }}>
            Iniciar mi Solicitud
          </Button>
        </Container>
      </Box>

      {/* ═══ CONSULTA DE ESTADO ═══ */}
      <Box id="insc-consulta" sx={{ py: { xs: 8, md: 12 }, background: '#fff' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label={t.consulta.chip} sx={{ background: `rgba(212,175,55,0.12)`, color: C.darkGold, fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, color: C.black, fontWeight: 800 }}>
              {t.consulta.title} <Box component="span" sx={{ color: C.darkGold, fontStyle: 'italic' }}>{t.consulta.titleHighlight}</Box> {t.consulta.titleEnd}
            </Typography>
          </Box>
          <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: `2px solid ${C.lightGold}`, background: C.cream }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              {t.consulta.subtitle}
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label={t.consulta.placeholder} value={docConsulta}
                  onChange={e => { setDocConsulta(e.target.value); setResultadoConsulta(null); setErrorConsulta(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleConsulta()}
                  disabled={consultando} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <Button fullWidth variant="contained" size="medium" onClick={handleConsulta}
                  disabled={consultando || !docConsulta.trim() || parseInt(consultaCaptchaResp,10) !== consultaCaptcha.r}
                  startIcon={consultando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold,
                    '&:hover': { background: C.darkGrey }, '&:disabled': { background: '#e0e0e0' } }}>
                  {consultando ? t.consulta.btnConsultando : t.consulta.btnConsultar}
                </Button>
              </Grid>
            </Grid>

            {/* CAPTCHA consulta */}
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: C.offWhite, border: `1px solid ${C.lightGold}` }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>VERIFICACIÓN DE SEGURIDAD</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold,
                  fontWeight: 800, fontSize: '1.1rem', px: 2, py: 1, borderRadius: 1.5, letterSpacing: 2, minWidth: 110, textAlign: 'center' }}>
                  {consultaCaptcha.a} {consultaCaptcha.op} {consultaCaptcha.b} = ?
                </Box>
                <TextField size="small" placeholder="Tu respuesta" sx={{ width: 120 }}
                  value={consultaCaptchaResp}
                  inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                  error={consultaCaptchaErr}
                  helperText={consultaCaptchaErr ? '❌ Respuesta incorrecta' : ' '}
                  onChange={e => { setConsultaCaptchaResp(e.target.value.replace(/\D/g, '')); setConsultaCaptchaErr(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleConsulta(); }} />
                <RefreshIcon onClick={() => { setConsultaCaptcha(newCaptcha()); setConsultaCaptchaResp(''); setConsultaCaptchaErr(false); }}
                  sx={{ color: C.darkGold, cursor: 'pointer', fontSize: 20, mt: '6px' }} titleAccess="Cambiar pregunta" />
              </Box>
            </Box>

            {errorConsulta && <Alert severity="error" sx={{ mt: 2 }}>{errorConsulta}</Alert>}

            {resultadoConsulta && (() => {
              // ── Lógica de pasos (bugs 1, 2, 3, 4 corregidos) ─────────────
              const r = resultadoConsulta;
              const pagado = r.pagoInscripcionConfirmado || pagoConfirmado;
              const sesionCompleta = r.sesionEstado === 'REALIZADA';

              // Bug 2 — evalAprobada: usar estado cuando pasosCompletados está vacío
              const evalAprobada = r.pasosCompletados?.includes('2')
                || ['EN_ORIENTACION', 'ACTIVO'].includes(r.estado);

              // Bug 3 — pagoDisponible: mismo criterio que evalAprobada
              const pagoDisponible = r.pasosCompletados?.includes('2')
                || ['EN_ORIENTACION', 'ACTIVO'].includes(r.estado);

              // Bug 4 — nextMsg: pago es la primera prioridad
              const nextMsg = !pagado
                ? 'Realice el pago de inscripción para continuar.'
                : !r.tieneSesionAgendada && !sesionCompleta
                  ? 'Agende su sesión de orientación por WhatsApp.'
                  : r.tieneSesionAgendada && !sesionCompleta
                    ? 'Su sesión está agendada. El envío de documentos estará disponible tras realizarla.'
                    : sesionCompleta && (r.documentosAprobados ?? 0) < 6
                      ? 'Complete el envío de documentos requeridos.'
                      : '¡Proceso completado! Bienvenido a la Alianza Solidaria de Capellanes.';

              const pasos = [
                {
                  label: 'Solicitud enviada',
                  done: true,
                  info: 'Tu solicitud fue recibida correctamente.',
                },
                {
                  label: 'Evaluación de documentos',
                  // Bug 2 corregido: marcar como completado si estado es EN_ORIENTACION o ACTIVO
                  done: evalAprobada,
                  info: evalAprobada
                    ? 'Evaluación aprobada ✓'
                    : 'El revisor evaluará sus documentos...',
                },
                {
                  label: 'Pago de inscripción',
                  done: pagado,
                  // Bug 3 corregido: mostrar texto disponible si estado es EN_ORIENTACION o ACTIVO
                  info: pagado
                    ? 'Pago confirmado ✓'
                    : pagoDisponible
                      ? 'Use el botón de pago abajo para realizar el pago'
                      : 'Disponible tras la aprobación de la evaluación',
                },
                {
                  label: 'Sesión de orientación',
                  done: sesionCompleta,
                  info: sesionCompleta
                    ? 'Sesión realizada ✓'
                    : r.tieneSesionAgendada
                      ? `Agendada${r.sesionFecha ? ' — ' + new Date(r.sesionFecha).toLocaleDateString('es-CO') : ''}`
                      : pagado ? 'Agenda tu sesión por WhatsApp' : 'Disponible tras el pago',
                },
                {
                  label: 'Envío de documentos',
                  done: (r.documentosAprobados ?? 0) >= 6,
                  info: sesionCompleta
                    ? `${r.documentosAprobados ?? 0}/6 documentos aprobados`
                    : 'Disponible tras la sesión de orientación',
                },
              ];

              return (
                <Box sx={{ mt: 3, p: 3, borderRadius: 3, border: `2px solid ${C.gold}`, background: '#fff' }}>
                  {/* Encabezado de la solicitud */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700 }}>
                        {r.nombres} {r.apellidos}
                      </Typography>
                      {r.fechaSolicitud && (
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          Solicitud: {new Date(r.fechaSolicitud).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={r.estado}
                      sx={{ fontWeight: 800, background: estadoColor[r.estado] || '#888', color: '#fff' }}
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Alertas de estado PENDIENTE / RECHAZADO */}
                  {r.estado === 'PENDIENTE' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>{t.consulta.estadoPendiente}</Alert>
                  )}
                  {r.estado === 'RECHAZADO' && (
                    <Alert severity="error" sx={{ mb: 2 }}>{t.consulta.estadoRechazado}</Alert>
                  )}

                  {/* Banner siguiente paso (Bug 4 corregido) */}
                  <Alert severity="info" icon={false}
                    sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem',
                      background: `rgba(212,175,55,0.08)`, color: C.black,
                      border: `1.5px solid rgba(212,175,55,0.30)` }}>
                    <strong>Siguiente paso:</strong> {nextMsg}
                  </Alert>

                  {/* Stepper de progreso (Bugs 2 y 3 corregidos) */}
                  <Box sx={{ mb: 2.5 }}>
                    {pasos.map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start',
                        p: 1.5, borderRadius: 2, mb: 1,
                        background: p.done ? 'rgba(212,175,55,0.06)' : 'rgba(0,0,0,0.02)',
                        border: `1.5px solid ${p.done ? 'rgba(212,175,55,0.30)' : 'rgba(0,0,0,0.07)'}` }}>
                        <Box sx={{ flexShrink: 0, mt: 0.2 }}>
                          {p.done
                            ? <CheckCircleFilledIcon sx={{ color: C.darkGold, fontSize: 18 }} />
                            : <CheckCircleIcon sx={{ color: '#bbb', fontSize: 18 }} />}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700,
                            color: p.done ? C.black : '#666' }}>
                            {i + 1}. {p.label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.2 }}>
                            {p.info}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Widget de pago — Bug 1 corregido: visible si no ha pagado,
                      independientemente de tieneSesionAgendada */}
                  {!pagado && r.afiliadoId && (
                    <Box sx={{ mb: 2 }}>
                      <PagoInscripcionWidget
                        afiliadoId={r.afiliadoId}
                        nombres={`${r.nombres} ${r.apellidos}`}
                        onPagado={() => {
                          setPagoConfirmado(true);
                          setResultadoConsulta(prev => prev ? { ...prev, pagoInscripcionConfirmado: true } : prev);
                        }}
                      />
                    </Box>
                  )}

                  {/* Agendar sesión — solo si ya pagó */}
                  {pagado && (
                    <Box>
                      {r.tieneSesionAgendada ? (
                        <Alert severity="success" icon={<CheckCircleFilledIcon />} sx={{ borderRadius: 2 }}>
                          <strong>¡Sesión agendada!</strong>
                          {r.sesionFecha && ` — ${new Date(r.sesionFecha).toLocaleDateString('es-CO')}`}
                          {r.sesionMedio && ` vía ${r.sesionMedio}`}
                        </Alert>
                      ) : (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                            ✅ Pago confirmado. Ya puedes agendar tu sesión de orientación.
                          </Alert>
                          <Button variant="contained" fullWidth size="large"
                            onClick={() => {
                              const nombre = r.nombres || '';
                              const cedula = docConsulta.trim();
                              const msg = `Hola, soy ${nombre} con cédula ${cedula}. Quiero agendar mi sesión de orientación para la afiliación a la Alianza Solidaria de Capellanes. Mi solicitud #${String(r.afiliadoId).padStart(6, '0')}.`;
                              window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_ADMIN || '573007765400'}?text=${encodeURIComponent(msg)}`, '_blank');
                            }}
                            sx={{ py: 1.8, borderRadius: 50,
                              background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`,
                              color: C.gold, border: `1px solid ${C.gold}`,
                              '&:hover': { background: C.darkGrey } }}>
                            Agendar Sesión de Orientación vía WhatsApp
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Card>
        </Container>
      </Box>

      {/* ── Modal Pre-verificación ─────────────────────────────────────────── */}
      {preVerifOpen && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300,
          background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Card sx={{ maxWidth: 460, width: '100%', p: 4, borderRadius: 4, border: `2px solid ${C.lightGold}` }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: C.black, mb: 1 }}>{t.preverif.title}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
              {t.preverif.subtitle}
            </Typography>
            <TextField fullWidth size="small" label={t.preverif.placeholder} value={preVerifDoc}
              onChange={e => { setPreVerifDoc(e.target.value); setPreVerifState('idle'); }}
              onKeyDown={e => e.key === 'Enter' && verificarElegibilidad()}
              disabled={preVerifState === 'checking'}
              sx={{ mb: 2 }} />

            {preVerifState === 'ok' && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>✅ Todo listo. Puedes continuar con tu solicitud.</Alert>
            )}
            {preVerifState === 'taken' && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: C.cream, border: `1.5px solid ${C.lightGold}`, display:'flex', flexDirection:'column', gap:1.5 }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#7c4a00', lineHeight: 1.6 }}>
                  ⚠️ Esta cédula ya tiene una solicitud registrada en la Alianza.
                </Typography>
                <Button variant="contained" size="small"
                  onClick={() => {
                    setPreVerifOpen(false); setPreVerifState('idle'); setPreVerifDoc('');
                    setPreVerifCaptcha(newCaptcha()); setPreVerifCaptchaResp(''); setPreVerifCaptchaErr(false);
                    setTimeout(() => document.getElementById('insc-consulta')?.scrollIntoView({ behavior:'smooth', block:'start' }), 200);
                  }}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 50, alignSelf: 'flex-start', background: C.darkGold, color: '#fff', '&:hover': { background: C.black } }}>
                  Ver mi estado de solicitud
                </Button>
              </Box>
            )}
            {preVerifState === 'takenOtraEntidad' && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                ✅ Esta cédula ya está registrada en otra entidad del sistema. Puedes continuar e inscribirte también a la <strong>Alianza de Capellanes</strong>.
              </Alert>
            )}
            {preVerifState === 'excluido' && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                Este documento no puede registrar una nueva solicitud en este momento. Si crees que hay un error, contáctanos directamente.
              </Alert>
            )}

            {/* CAPTCHA pre-verificación */}
            <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, background: C.offWhite, border: `1px solid ${C.lightGold}` }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>VERIFICACIÓN DE SEGURIDAD</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold,
                  fontWeight: 800, fontSize: '1.1rem', px: 2, py: 1, borderRadius: 1.5, letterSpacing: 2, minWidth: 110, textAlign: 'center' }}>
                  {preVerifCaptcha.a} {preVerifCaptcha.op} {preVerifCaptcha.b} = ?
                </Box>
                <TextField size="small" placeholder="Tu respuesta" sx={{ width: 120 }}
                  value={preVerifCaptchaResp}
                  inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                  error={preVerifCaptchaErr}
                  helperText={preVerifCaptchaErr ? '❌ Respuesta incorrecta' : ' '}
                  onChange={e => { setPreVerifCaptchaResp(e.target.value.replace(/\D/g, '')); setPreVerifCaptchaErr(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') verificarElegibilidad(); }} />
                <RefreshIcon
                  onClick={() => { setPreVerifCaptcha(newCaptcha()); setPreVerifCaptchaResp(''); setPreVerifCaptchaErr(false); }}
                  sx={{ color: C.darkGold, cursor: 'pointer', fontSize: 20, mt: '6px' }} titleAccess="Cambiar pregunta" />
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button variant="outlined"
                onClick={() => { setPreVerifOpen(false); setPreVerifState('idle'); setPreVerifDoc(''); setPreVerifCaptcha(newCaptcha()); setPreVerifCaptchaResp(''); setPreVerifCaptchaErr(false); }}
                sx={{ borderRadius: 50, borderColor: C.gold, color: C.darkGold }}>
                Cancelar
              </Button>
              {(preVerifState === 'ok' || preVerifState === 'takenOtraEntidad') ? (
                <Button variant="contained" endIcon={<ArrowForwardIcon />}
                  onClick={abrirFormulario}
                  sx={{ borderRadius: 50, background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold }}>
                  Continuar con la solicitud
                </Button>
              ) : (
                <Button variant="contained"
                  disabled={preVerifDoc.trim().length < 5 || preVerifState === 'checking' || preVerifState === 'excluido' || preVerifState === 'taken' || parseInt(preVerifCaptchaResp, 10) !== preVerifCaptcha.r}
                  onClick={verificarElegibilidad}
                  sx={{ borderRadius: 50, background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold }}>
                  {preVerifState === 'checking' ? <CircularProgress size={18} color="inherit" /> : 'Verificar elegibilidad'}
                </Button>
              )}
            </Stack>
          </Card>
        </Box>
      )}

      {/* Contrato (Dialog) */}
      <ContratoAfiliacion
        open={contratoOpen}
        onClose={() => setContratoOpen(false)}
        cooperativaIdFijo={CAPELLANIAS_COOPERATIVA_ID}
        onSuccess={(info) => { setSolicitudInfo(info); setContratoOpen(false); }}
      />
    </Box>
  );
}
